import {
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { UnifiedChartData } from "./types";
import { formatNumber } from "../../../api/DataTypeUtils";
import { colors } from "../../../board/components/TaskDetail/ColorPicker";

function CustomXAxisTickHorizontal({ x, y, payload, angle }) {
  const maxChars = 10;
  const text =
    payload.value.length > maxChars ? payload.value.slice(0, maxChars) + "…" : payload.value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        transform={`rotate(${angle})`}
        fontSize={12}
        fill="#6B7280"
        fontWeight={500}
      >
        {text}
        <title>{payload.value}</title>
      </text>
    </g>
  );
}

function CustomYAxisTickVertical({ x, y, payload }) {
  const maxChars = 10;
  const text =
    payload.value.length > maxChars ? payload.value.slice(0, maxChars) + "…" : payload.value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" fontSize={12} fill="#6B7280" fontWeight={500}>
        {text}
        <title>{payload.value}</title>
      </text>
    </g>
  );
}

type Props = {
  data: UnifiedChartData;
  stacked?: boolean;
  height?: number;
  angle?: number;
  barSize?: number;
  categoryGap?: number | string;
  barColors?: string[]; // apply per-bar colors when using single series
  layout?: "horizontal" | "vertical"; // horizontal -> left-to-right bars
  isByDate?: boolean;
  labelTooltip?: string;
  segmentLabelTooltip?: string;
};

const BarChart = ({
  data,
  stacked = false,
  height = 440,
  angle = 30,
  barSize = 12,
  categoryGap = "55%",
  barColors = colors,
  layout = "horizontal",
  isByDate,
  labelTooltip,
  segmentLabelTooltip,
}: Props) => {
  const rows = data.labels.map((label, i) => {
    const row: any = { label };
    data.series.forEach((s) => {
      row[s.dataKey] = s.data[i] ?? 0;
      row["color"] = barColors[i % barColors.length];
    });
    return row;
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="recharts-default-tooltip px-3 py-2 rounded-md bg-[#111827]">
          <p className="text-[#D1D5DB]">{`${
            labelTooltip ? `${labelTooltip} : ${label}` : label
          }`}</p>
          {payload.map((item, index) => {
            if (!isByDate && !item.value) {
              return null;
            }
            let segmentLabel = item.name;
            if (segmentLabel == "None") {
              segmentLabel = segmentLabelTooltip;
            }
            let fillColor = item.payload?.color || colors[0];
            if (isByDate) {
              fillColor = barColors[index % barColors.length];
            }
            return (
              <p key={`item-${index}`} className="text-[#ffffff]">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: fillColor }}
                ></span>
                <span className="ml-2">{`${
                  segmentLabel
                    ? `${segmentLabel} : ${formatNumber(item.value)}`
                    : formatNumber(item.value)
                }`}</span>
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RBarChart
        data={rows}
        margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
        barCategoryGap={categoryGap}
        layout={layout === "horizontal" ? "horizontal" : "vertical"}
      >
        <CartesianGrid stroke="#F3F4F6" />
        {layout === "horizontal" ? (
          <>
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={{ stroke: "#E5E7EB" }}
              height={60}
              textAnchor="start"
              tick={(props) => <CustomXAxisTickHorizontal {...props} angle={angle} />}
            />
            <YAxis
              tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
              tickLine={false}
              axisLine={{ stroke: "#E5E7EB" }}
              tickFormatter={(value) => formatNumber(value)}
            />
          </>
        ) : (
          <>
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
              tickLine={false}
              axisLine={{ stroke: "#E5E7EB" }}
              tickFormatter={(value) => formatNumber(value)}
            />
            <YAxis
              type="category"
              dataKey="label"
              tickLine={false}
              axisLine={false}
              width={90}
              tick={(props) => <CustomYAxisTickVertical {...props} />}
            />
          </>
        )}
        <Tooltip content={CustomTooltip} />
        {data.series.map((s, idx) => (
          <Bar
            key={s.name}
            dataKey={s.dataKey}
            name={s.name}
            stackId={stacked ? "a" : undefined}
            barSize={barSize}
          >
            {rows.map((_entry, index) => {
              let fillColor = barColors[index % barColors.length];
              if (isByDate) {
                fillColor = barColors[idx % barColors.length];
              }
              return <Cell key={`cell-${index}`} fill={fillColor} />;
            })}
          </Bar>
        ))}
      </RBarChart>
    </ResponsiveContainer>
  );
};

export default BarChart;
